// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: what a message sent while the agent works does (JP 2026-10-30). Steer (the default) adds it
// to the running turn; Queue sends it once the turn ends. Enter does the chosen one and ⌘/Ctrl+Enter
// the other. Settings → Connections → Agents → Manage, above the machines; saved in the Agents prefs.

import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import styles from '@app/features/konvo_agents/components/AgentsConnection.module.css';
import {modifierKey} from '@app/features/konvo_agents/components/AgentsCopy';
import Agents from '@app/features/konvo_agents/state/Agents';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {RadioGroup, type RadioOption} from '@app/features/ui/radio_group/RadioGroup';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useEffect} from 'react';

type FollowUp = 'steer' | 'queue';

export const AgentFollowUpSettings = observer(() => {
	const {t} = useLingui();
	useEffect(() => {
		if (!Agents.prefs) void Agents.loadPrefs();
	}, []);
	const value = Agents.followUp;
	const mod = modifierKey();
	const keys = (choice: FollowUp) =>
		choice === value
			? t({
					message: 'Enter',
					comment: 'Agents Settings: the key that sends a message this way (the chosen default).',
				})
			: t({
					message: `${mod} Enter`,
					comment: 'Agents Settings: the keys that send a message this way (not the default). The first is ⌘ or Ctrl.',
				});
	const steerKeys = keys('steer');
	const queueKeys = keys('queue');
	const options: ReadonlyArray<RadioOption<FollowUp>> = [
		{
			value: 'steer',
			name: t({message: 'Steer', comment: 'Agents Settings: option that adds the message to the running turn.'}),
			desc: t({
				message: `Adds your message to what the agent is doing now, so it changes course straight away. ${steerKeys}.`,
				comment: 'Agents Settings: what Steer does. The last part is the key that does it: Enter, or ⌘/Ctrl Enter.',
			}),
		},
		{
			value: 'queue',
			name: t({message: 'Queue', comment: 'Agents Settings: option that sends the message after the running turn.'}),
			desc: t({
				message: `Holds your message until the agent finishes, then sends it. You can send it sooner or remove it while it waits. ${queueKeys}.`,
				comment: 'Agents Settings: what Queue does. The last part is the key that does it: Enter, or ⌘/Ctrl Enter.',
			}),
		},
	];
	return (
		<SettingsSection
			id="agents-follow-up"
			title={t({
				message: 'Messages while an agent works',
				comment: 'Settings → Agents: heading of the choice between steering and queueing.',
			})}
			description={t({
				message: `Enter does the one you pick, and ${mod} Enter does the other.`,
				comment: 'Settings → Agents: what the two keys do. The key is ⌘ or Ctrl.',
			})}
		>
			<div className={styles.followUp} data-flx="konvo-agents.follow-up">
				<RadioGroup
					options={options}
					value={Agents.prefs ? value : null}
					disabled={!Agents.prefs}
					onChange={(next) =>
						void Agents.savePrefs({followUp: next}).catch(() =>
							ToastCommands.error(
								t({message: "Couldn't save that. Try again.", comment: 'Agents Settings: saving the choice failed.'}),
							),
						)
					}
					aria-label={t({
						message: 'What Enter does while an agent works',
						comment: 'Agents Settings: label of the steer or queue choice.',
					})}
				/>
			</div>
		</SettingsSection>
	);
});
